// File: RuleGrid.js
import React, { useState, useMemo } from "react";
import dayjs from "dayjs";
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Button,
  Tooltip,
  Divider,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditNoteIcon from "@mui/icons-material/EditNote";
import LinkIcon from "@mui/icons-material/Link";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "/src/assets/icons/search.svg";
import PoetIcon from "components/PoetIcon/PoetIcon";
import {
  BLANK_RULE,
  LOB_CONFIG_DATA,
  getRuleUnitConfigForTab,
} from "../Constants/PolicyTranslationConstants";
import { SettingsApplications } from "@mui/icons-material";
import ApplyAdoptionDecisionsDialog from "../Dialogs/ApplyAdoptionDecisionDialog";
import GenericDialog from "components/GenericDialog";
import ViewOtherInfo from "../Dialogs/ViewOtherInfo";
import { useDispatch } from "react-redux";
import { setDialog } from "slices/appSlice";
import CodeListDialog from "./CodeListDialog";

const COLUMN_WIDTHS = {
  default: 150,
  compact: 75, // Version / Diagnoses / Other
  dateLOB: 220,
};

/**
 * Resolves the rationale text stored on a rule unit.
 */
const getRuleRationaleText = (rule) =>
  rule?.ruleRationale || rule?.rationaleText || "";

/**
 * Builds the rationale dialog title from the rule name and version.
 */
const getRationaleDialogTitle = (rule) => {
  const ruleName = rule?.ruleUnitName || rule?.ruleName || "";
  const ruleVersion =
    rule?.version ||
    (rule?.ruleUnitVersion !== null && rule?.ruleUnitVersion !== undefined
      ? String(rule.ruleUnitVersion)
      : "");
  const ruleLabel = [ruleName, ruleVersion].filter(Boolean).join(" ");

  return `Rationale${ruleLabel ? ` - ${ruleLabel}` : ""}`;
};

const colSx = (width) => ({
  width,
  flex: "0 0 auto",
  minWidth: 0,
  maxWidth: "100%",
});

const ColumnHeader = ({ label }) => (
  <Typography variant="subtitle2" fontWeight="bold">
    {label}
  </Typography>
);

const RuleRow = ({
  rule,
  columnsToShow,
  onEditNoteClick,
  onViewOtherInfo,
  onViewRationale,
  onViewDiagnoses,
  setActiveRow,
  setOpenApplyAdoptionDecisionDialog,
}) => {
  const rationaleText = getRuleRationaleText(rule).trim();

  const getDateRange = (adoptionDetails) => {
    if (!adoptionDetails || adoptionDetails.length === 0) return "";
    const earliestEffectiveDate = adoptionDetails.reduce(
      (earliest, current) => {
        return dayjs(current.effectiveDate).isBefore(
          dayjs(earliest.effectiveDate)
        )
          ? current
          : earliest;
      },
      adoptionDetails[0]
    );

    // Only compare real end dates (ignore "", null, undefined, invalid).
    const endDateCandidates = adoptionDetails.filter((d) => {
      if (!d?.endDate) return false;
      return dayjs(d.endDate).isValid();
    });

    const earliestEndDate =
      endDateCandidates.length > 0
        ? endDateCandidates.reduce((earliest, current) =>
            dayjs(current.endDate).isBefore(dayjs(earliest.endDate))
              ? current
              : earliest
          )
        : null;
    const effectiveDate = dayjs(earliestEffectiveDate.effectiveDate).format(
      "MM/DD/YY"
    );

    const endDate = earliestEndDate
      ? dayjs(earliestEndDate.endDate).format("MM/DD/YY")
      : "";

    return endDate ? `${effectiveDate} - ${endDate}` : effectiveDate;
  };

  return (
    <Grid container spacing={2} alignItems="center" wrap="wrap" sx={{ py: 1 }}>
      {columnsToShow.ruleName && (
        <Grid item xs={1.5} sx={colSx(COLUMN_WIDTHS.default)}>
          <Typography
            variant="body2"
            onClick={() => onEditNoteClick && onEditNoteClick(rule)}
            sx={{ cursor: "pointer", textDecoration: "underline" }}
          >
            {rule.ruleName}
          </Typography>
        </Grid>
      )}
      {columnsToShow.version && (
        <Grid item xs={1.5} sx={colSx(COLUMN_WIDTHS.compact)}>
          <Typography variant="body2">{rule.version}</Typography>
        </Grid>
      )}
      {columnsToShow.cc && (
        <Grid item xs={1} sx={colSx(COLUMN_WIDTHS.default)}>
          <Typography variant="body2">{rule.cc}</Typography>
        </Grid>
      )}
      {columnsToShow.procedures && (
        <Grid item xs={1.5} sx={colSx(COLUMN_WIDTHS.default)}>
          <Typography variant="body2">{rule.procedures}</Typography>
        </Grid>
      )}
      {columnsToShow.ruleType && (
        <Grid item xs={1} sx={colSx(COLUMN_WIDTHS.default)}>
          <Typography variant="body2">{rule.ruleType}</Typography>
        </Grid>
      )}
      {columnsToShow.diagnoses && (
        <Grid item xs={0.75} sx={colSx(COLUMN_WIDTHS.compact)}>
          {rule.diagnoses && (
            <Tooltip title="View Diagnoses">
              <IconButton
                size="small"
                onClick={() => onViewDiagnoses && onViewDiagnoses(rule)}
              >
                <img
                  src={SearchIcon}
                  alt="View Diagnoses"
                  style={{ width: 20, height: 20 }}
                />
              </IconButton>
            </Tooltip>
          )}
        </Grid>
      )}
      {columnsToShow.modifiers && (
        <Grid item xs={0.75} sx={colSx(COLUMN_WIDTHS.compact)}>
          <Tooltip title="View Other Info">
            <IconButton
              size="small"
              onClick={() => {
                setActiveRow(rule);
                onViewOtherInfo(rule);
              }}
            >
              <img
                src={SearchIcon}
                alt="View Other Info"
                style={{ width: 20, height: 20 }}
              />
            </IconButton>
          </Tooltip>
        </Grid>
      )}
      {columnsToShow.dateLOB && (
        <Grid
          item
          xs={1}
          sx={{
            ...colSx(COLUMN_WIDTHS.dateLOB),
            overflow: "hidden",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="caption" color="textSecondary">
                {getDateRange(rule.adoptionDetails)}
              </Typography>
              <IconButton
                data-testid="open-adoption-decision-dialog"
                size="small"
                sx={{ p: 0.25 }}
                onClick={() => {
                  setActiveRow(rule);
                  setOpenApplyAdoptionDecisionDialog(true);
                }}
              >
                <SettingsApplications fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Grid>
      )}

      {(columnsToShow.rationale || columnsToShow.actions) && (
        <Grid item xs={1} sx={colSx(COLUMN_WIDTHS.default)}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            {columnsToShow.rationale && rationaleText && (
              <Tooltip title="View Rationale">
                <IconButton size="small" onClick={() => onViewRationale(rule)}>
                  <img
                    src={SearchIcon}
                    alt="View Rationale"
                    style={{ width: 20, height: 20 }}
                  />
                </IconButton>
              </Tooltip>
            )}
            {columnsToShow.actions?.editNote && (
              <Tooltip title="Edit Note">
                <IconButton
                  size="small"
                  onClick={() => onEditNoteClick && onEditNoteClick(rule)}
                >
                  <EditNoteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {columnsToShow.actions?.link && (
              <Tooltip title="Dataset Linked">
                <IconButton size="small">
                  <LinkIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

const defaultColumnsToShow = {
  ruleName: true,
  cc: true,
  version: true,
  procedures: true,
  ruleType: true,
  diagnoses: true,
  modifiers: true,
  dateLOB: true,
  rationale: true,
  actions: { editNote: true, link: true }, // now an object
};

const getAllowedRuleUnitTypesForTab = (tabValue) => {
  if (tabValue === null || tabValue === undefined) {
    return null;
  }

  const ruleConfig = getRuleUnitConfigForTab(tabValue);
  if (!ruleConfig?.ruleUnitType) {
    return [];
  }

  return [String(ruleConfig.ruleUnitType).toUpperCase()];
};

const getRuleUnitTypeValue = (rule) => {
  const ruleUnitType = rule?.ruleUnitType;

  if (typeof ruleUnitType === "string") {
    return ruleUnitType.toUpperCase();
  }

  if (ruleUnitType?.ruleType) {
    return String(ruleUnitType.ruleType).toUpperCase();
  }

  if (ruleUnitType?.sheetName) {
    return String(ruleUnitType.sheetName).toUpperCase();
  }

  if (rule?.ruleType) {
    return String(rule.ruleType).toUpperCase();
  }

  return "";
};

const RuleGrid = ({
  columnsToShow = defaultColumnsToShow,
  onEditNoteClick,
  handleAddNewRule,
  editable,
  policyData,
  healthPlanId,
  tabValue,
  rules,
  setRules,
}) => {
  const dispatch = useDispatch();
  const [openApplyAdoptionDecisionDialog, setOpenApplyAdoptionDecisionDialog] =
    useState(false);
  const [activeRow, setActiveRow] = useState({}); //Will be used for the adoption decision dialog.
  const [openCodeListDialog, setOpenCodeListDialog] = useState(false);
  const [activeDiagnoses, setActiveDiagnoses] = useState(null);
  const [openOtherInfoDialog, setOpenOtherInfoDialog] = useState(false);
  const [activeOtherInfoRule, setActiveOtherInfoRule] = useState(null);
  const stableDiagnosisCodeGroups = useMemo(
    () => activeDiagnoses ?? [],
    [activeDiagnoses]
  );

  /**
   * Resolves whether two rule rows represent the same rule unit.
   */
  const isSameRuleUnit = (currentRule, targetRule) => {
    const currentRuleUnitKey = currentRule?.ruleUnitKey;
    const targetRuleUnitKey = targetRule?.ruleUnitKey;

    if (
      currentRuleUnitKey !== undefined &&
      currentRuleUnitKey !== null &&
      currentRuleUnitKey !== "" &&
      targetRuleUnitKey !== undefined &&
      targetRuleUnitKey !== null &&
      targetRuleUnitKey !== ""
    ) {
      return String(currentRuleUnitKey) === String(targetRuleUnitKey);
    }

    const currentIndex = currentRule?.index;
    const targetIndex = targetRule?.index;

    if (
      currentIndex !== undefined &&
      currentIndex !== null &&
      targetIndex !== undefined &&
      targetIndex !== null
    ) {
      return String(currentIndex) === String(targetIndex);
    }

    return currentRule === targetRule;
  };

  /**
   * Opens the CodeListDialog for a rule's diagnoses data.
   */
  const onViewDiagnoses = (rule) => {
    setActiveDiagnoses(rule.diagnosisCodeGroups ?? null);
    setOpenCodeListDialog(true);
  };

  /**
   * Opens the Other Details dialog for the selected rule unit.
   */
  const onViewOtherInfo = (rule) => {
    setActiveOtherInfoRule(rule);
    setOpenOtherInfoDialog(true);
  };

  /**
   * Opens the rationale dialog for a rule unit with rationale text.
   */
  const onViewRationale = (rule) => {
    dispatch(
      setDialog({
        title: getRationaleDialogTitle(rule),
        message: getRuleRationaleText(rule),
      })
    );
  };

  /**
   * Marks a rule unit as dirty when a persisted row is modified.
   */
  const getNextEditRuleUnitState = (rule) => {
    if (!rule?.ruleUnitKey) {
      return "NEW";
    }

    return (rule.editRuleUnit || "").toUpperCase() === "NEW" ? "NEW" : "EDIT";
  };

  const handleAdoptionDecisionSave = (adoptionData, row) => {
    let adoptionDetails = [];
    const currentDate = dayjs().format("YYYY-MM-DDTHH:mm:ss");
    const isGlobal = adoptionData.find(
      (item) => item.line === "Global" && item.toggled
    )
      ? true
      : false;

    if (isGlobal) {
      const globalEffectiveDate = adoptionData.find(
        (item) => item.line === "Global" && item.toggled
      ).date;

      adoptionDetails.push({
        LobConfigKey: 0,
        effectiveDate: globalEffectiveDate,
        endDate: "",
      });
    } else {
      adoptionData.forEach((item) => {
        if (item.line === "Global") {
          return;
        }

        const hasEndDate = Boolean(item.endDate);
        adoptionDetails.push({
          LobConfigKey: item.id,
          effectiveDate: item.date,
          endDate: hasEndDate ? item.endDate : item.toggled ? "" : currentDate,
        });
      });
    }

    // Update the rules state with the modified row.
    // Use a functional update to avoid stale `rules` closures and merge into the
    // latest row so we don't accidentally drop fields.
    setRules((prevRules) =>
      prevRules.map((r) =>
        isSameRuleUnit(r, row)
          ? {
              ...r,
              adoptionDetails,
              editRuleUnit: getNextEditRuleUnitState(r),
            }
          : r
      )
    );
    setOpenApplyAdoptionDecisionDialog(false);
  };

  // Normalize actions to always be an object for easier logic
  const actions =
    typeof columnsToShow.actions === "object"
      ? columnsToShow.actions
      : { editNote: !!columnsToShow.actions, link: !!columnsToShow.actions };

  const allowedRuleUnitTypes = getAllowedRuleUnitTypesForTab(tabValue);
  const filteredRules =
    allowedRuleUnitTypes === null
      ? rules
      : rules.filter((rule) =>
          allowedRuleUnitTypes.includes(getRuleUnitTypeValue(rule))
        );

  return (
    <Box
      p={3}
      sx={{ display: "flex", flexDirection: "column", height: "100%" }}
    >
      {/* Add Rule Button */}
      <Box
        display="flex"
        alignItems="center"
        mb={2}
        onClick={() => handleAddNewRule && handleAddNewRule(BLANK_RULE)}
      >
        <Tooltip title="Add Rule">
          <IconButton>
            <PoetIcon iconName="add" />
          </IconButton>
        </Tooltip>
        <Typography variant="h6" sx={{ fontWeight: "bold", ml: 1 }}>
          ADD RULE
        </Typography>
      </Box>
      {/* Header Row (not scrollable, with border) */}
      <Box
        sx={{
          borderBottom: "1px solid #ccc",
          background: "#fff",
          position: "sticky",
          top: 0,
          zIndex: 1,
        }}
      >
        <Grid
          container
          spacing={2}
          alignItems="center"
          wrap="wrap"
          sx={{ pb: 1 }}
        >
          {columnsToShow.ruleName && (
            <Grid item xs={1.5} sx={colSx(COLUMN_WIDTHS.default)}>
              <ColumnHeader label="Rule Name" />
            </Grid>
          )}
          {columnsToShow.version && (
            <Grid item xs={1} sx={colSx(COLUMN_WIDTHS.compact)}>
              <ColumnHeader label="Version" />
            </Grid>
          )}
          {columnsToShow.cc && (
            <Grid item xs={1} sx={colSx(COLUMN_WIDTHS.default)}>
              <ColumnHeader label="CC" />
            </Grid>
          )}
          {columnsToShow.procedures && (
            <Grid item xs={1.5} sx={colSx(COLUMN_WIDTHS.default)}>
              <ColumnHeader label="Procedure Group(s)" />
            </Grid>
          )}
          {columnsToShow.ruleType && (
            <Grid item xs={1} sx={colSx(COLUMN_WIDTHS.default)}>
              <ColumnHeader label="Rule Type" />
            </Grid>
          )}
          {columnsToShow.diagnoses && (
            <Grid item xs={0.75} sx={colSx(COLUMN_WIDTHS.compact)}>
              <ColumnHeader label="Diagnoses" />
            </Grid>
          )}
          {columnsToShow.modifiers && (
            <Grid item xs={0.75} sx={colSx(COLUMN_WIDTHS.compact)}>
              <ColumnHeader label="Other" />
            </Grid>
          )}
          {columnsToShow.dateLOB && (
            <Grid item xs={1} sx={colSx(COLUMN_WIDTHS.dateLOB)}>
              <ColumnHeader label="Date/LOB" />
            </Grid>
          )}

          {(columnsToShow.rationale || actions.editNote || actions.link) && (
            <Grid item xs={1} sx={colSx(COLUMN_WIDTHS.default)}>
              <ColumnHeader label="" />
            </Grid>
          )}
        </Grid>
      </Box>
      {/* Data Rows (scrollable) */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          background: "#fff",
        }}
      >
        {filteredRules.map((rule, idx) => (
          <RuleRow
            key={rule.index ?? idx}
            rule={rule}
            columnsToShow={columnsToShow}
            onEditNoteClick={onEditNoteClick}
            onViewRationale={onViewRationale}
            onViewDiagnoses={onViewDiagnoses}
            setActiveRow={setActiveRow}
            setOpenApplyAdoptionDecisionDialog={
              setOpenApplyAdoptionDecisionDialog
            }
            onViewOtherInfo={onViewOtherInfo}
          />
        ))}
      </Box>
      <ApplyAdoptionDecisionsDialog
        key={`${activeRow?.index ?? "none"}-${openApplyAdoptionDecisionDialog ? "open" : "closed"}`}
        editable={editable}
        open={openApplyAdoptionDecisionDialog}
        onClose={() => setOpenApplyAdoptionDecisionDialog(false)}
        policyData={policyData}
        //variant={variant} --- IGNORE ---
        handleAdoptionDecisionSave={handleAdoptionDecisionSave}
        row={activeRow}
      />
      <GenericDialog />
      <ViewOtherInfo
        open={openOtherInfoDialog}
        onClose={() => setOpenOtherInfoDialog(false)}
        title="Other Details"
        ruleUnit={activeOtherInfoRule}
      />
      <CodeListDialog
        open={openCodeListDialog}
        onClose={() => setOpenCodeListDialog(false)}
        diagnosisCodeGroups={stableDiagnosisCodeGroups}
      />
      <Divider sx={{ mt: 2 }} />
    </Box>
  );
};

export default RuleGrid;
